import { useMemo } from "react";

import { BsMetaUtil, isLeverageLoop, usePrincipalCollateralBorrowRate } from "@bridgesplit/abf-react";
import { BRAND_COLORS, FormatType, SeriesConfig, TimeseriesChart, useAppPalette } from "@bridgesplit/ui";
import { percentUiToDecimals, roundTimestamp } from "@bridgesplit/utils";

import { useLoopContext } from "../../LoopContext";

type RatesInfo = {
    date: number;
    avgNetApy?: number;
    collateralApy?: number;
    avgBorrowApy?: number;
};

export default function LoopApyHistory({ period, xDomain }: { period: number; xDomain: [number, number] }) {
    const rates = useLoopApyHistory(period);
    const { loopExpanded } = useLoopContext();
    const { secondary } = useAppPalette();

    const series: SeriesConfig<RatesInfo>[] = useMemo(() => {
        if (isLeverageLoop(loopExpanded)) {
            return [{ dataKey: "avgBorrowApy", label: "Avg. Borrow APY", color: secondary }];
        }
        const collateralSymbol = BsMetaUtil.getSymbol(loopExpanded?.collateralToken);
        return [
            { dataKey: "avgNetApy", label: "Avg. Net APY", color: secondary },
            { dataKey: "collateralApy", label: `${collateralSymbol} APY`, color: BRAND_COLORS.BLUE, dash: true },
            { dataKey: "avgBorrowApy", label: "Avg. Borrow APY", color: BRAND_COLORS.ORANGE }
        ];
    }, [loopExpanded, secondary]);

    return (
        <TimeseriesChart<RatesInfo>
            chartId="loop-apy-history"
            data={rates ?? []}
            height={200}
            axisFormat={FormatType.Percent}
            series={series}
            xDomain={xDomain}
        />
    );
}

function useLoopApyHistory(period: number) {
    const { loopExpanded } = useLoopContext();

    const collateralMint = loopExpanded?.collateralToken.mint;

    const { data: borrowRatesRecord } = usePrincipalCollateralBorrowRate(
        {
            principalMint: loopExpanded?.principalToken.mint,
            collateralMints: collateralMint ? [collateralMint] : undefined,
            timeLookback: period
        },
        { skip: !loopExpanded || !collateralMint }
    );
    const borrowRates = collateralMint ? borrowRatesRecord?.perCollateral?.[collateralMint] : undefined;

    return useMemo(() => {
        if (!borrowRates) return undefined;
        const seenTimestamps = new Set<number>();
        const data: RatesInfo[] = [];

        for (const { timestamp, wAvgBorrowRate, wAvgApy, collateralYield } of borrowRates) {
            const roundedTimestamp = roundTimestamp(timestamp);
            if (seenTimestamps.has(roundedTimestamp)) continue;
            seenTimestamps.add(roundedTimestamp);
            const collateralApy = collateralYield != null ? percentUiToDecimals(collateralYield) : undefined;
            const borrowApy = wAvgBorrowRate != null ? percentUiToDecimals(wAvgBorrowRate) : undefined;
            const netApy = wAvgApy != null ? percentUiToDecimals(wAvgApy) : undefined;

            // Borrow APY is always the driving series for the chart.
            if (borrowApy == null) continue;

            data.push({
                date: roundedTimestamp,
                avgNetApy: netApy,
                collateralApy,
                avgBorrowApy: borrowApy
            });
        }

        return data;
    }, [borrowRates]);
}
